import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { Email, MarkEmailRead, Phone, Sms } from '@mui/icons-material';
import { Task } from '@otw/models/tasks';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';

interface ViewSentCommunicationDialogProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
}

type Channel = 'email' | 'text';

const ViewSentCommunicationDialog: React.FC<ViewSentCommunicationDialogProps> = ({
  open,
  task,
  onClose,
}) => {
  const theme = useTheme();

  const hasEmail = Boolean(task?.completionEmail);
  const hasText = Boolean(task?.completionText);

  const initialTab: Channel = hasEmail ? 'email' : 'text';
  const [tab, setTab] = useState<Channel>(initialTab);

  useEffect(() => {
    if (!open) return;
    setTab(hasEmail ? 'email' : 'text');
  }, [open, hasEmail]);

  const sentAtLabel = useMemo(() => {
    const date =
      task?.completionEmail?.sentAt || task?.completionText?.sentAt || task?.completedAt || null;
    if (!date) return null;
    const d = dayjs(date);
    return d.isValid() ? d.format('MMM D, YYYY [at] h:mm A') : null;
  }, [task]);

  if (!task || (!hasEmail && !hasText)) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.15),
              color: theme.palette.success.main,
            }}
          >
            <MarkEmailRead />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Sent communication
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {task.title}
              {sentAtLabel ? ` · ${sentAtLabel}` : ''}
            </Typography>
          </Box>
          {hasEmail && hasText && (
            <Chip
              size="small"
              label="Email + Text"
              sx={{
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.dark,
              }}
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {hasEmail && hasText && (
          <Tabs
            value={tab}
            onChange={(_, value: Channel) => setTab(value)}
            sx={{ minHeight: 36, mb: 1.5 }}
          >
            <Tab
              icon={<Email fontSize="small" />}
              iconPosition="start"
              value="email"
              label="Email"
              sx={{ minHeight: 36 }}
            />
            <Tab
              icon={<Sms fontSize="small" />}
              iconPosition="start"
              value="text"
              label="Text"
              sx={{ minHeight: 36 }}
            />
          </Tabs>
        )}

        {tab === 'email' && task.completionEmail && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                icon={<Email fontSize="inherit" />}
                label={`To: ${task.completionEmail.recipientEmail}`}
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.dark,
                }}
              />
              <Chip
                size="small"
                label={`Subject: ${task.completionEmail.subject}`}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              {task.completionEmail.senderName && (
                <Chip
                  size="small"
                  label={`Sent by ${task.completionEmail.senderName}`}
                  variant="outlined"
                />
              )}
            </Stack>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.default, 0.6),
                overflow: 'hidden',
                minHeight: 360,
              }}
            >
              <Box
                component="iframe"
                title="Sent email"
                srcDoc={task.completionEmail.html}
                sx={{
                  width: '100%',
                  height: { xs: 420, md: 560 },
                  border: 'none',
                  display: 'block',
                  bgcolor: '#f5f5f5',
                }}
              />
            </Box>
          </Stack>
        )}

        {tab === 'text' && task.completionText && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                icon={<Phone fontSize="inherit" />}
                label={`To: ${task.completionText.recipientPhone}`}
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.dark,
                }}
              />
              <Chip
                size="small"
                label={`${task.completionText.body.length} characters`}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              {task.completionText.senderName && (
                <Chip
                  size="small"
                  label={`Sent by ${task.completionText.senderName}`}
                  variant="outlined"
                />
              )}
            </Stack>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                maxWidth: 460,
                bgcolor: alpha(theme.palette.success.main, 0.06),
                borderColor: alpha(theme.palette.success.main, 0.25),
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {task.completionText.body}
              </Typography>
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewSentCommunicationDialog;
